import React, { useLayoutEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Text,
  HStack,
  Icon,
  Input,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';

type Option = {
  id: string;
  label: string | React.ReactNode;
  icon?: React.ElementType;
  hex_code?: string;
  searchLabel?: string;
};

type SelectMenuProps = {
  label?: string;
  options: Option[];
  placeholder?: string;
  selectedValue?: string | null;
  onChange: (id: string) => void;
  size?: 'sm' | 'md';
  isFullWidth?: boolean;
  isDisabled?: boolean;
  isSearchable?: boolean;
  noOptionsMessage?: string; // New prop for custom no-options message
};

const CustomSelect: React.FC<SelectMenuProps> = ({
  label,
  options,
  placeholder = 'Select an option',
  selectedValue = null,
  onChange,
  size = 'md',
  isFullWidth = false,
  isDisabled = false,
  isSearchable = false,
  noOptionsMessage = 'No options available', // Default to existing message
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuWidth, setMenuWidth] = useState<number | null>(null);

  const onOpen = () => {
    if (!isDisabled) {
      setIsOpen(true);
      setSearchTerm('');
    }
  };
  const onClose = () => {
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleSelect = (id: string) => {
    if (!isDisabled) {
      onChange(id);
      onClose();
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  useLayoutEffect(() => {
    if (menuRef.current) {
      setMenuWidth(menuRef.current.offsetWidth);
    }
  }, [options, selectedValue, isFullWidth, isOpen]);

  const selectedOption = selectedValue
    ? options.find((option) => option.id === selectedValue)
    : null;

  const buttonStyles = {
    sm: {
      height: '32px',
      fontSize: 'sm',
      px: 2,
      iconSpacing: '12px',
    },
    md: {
      height: '40px',
      fontSize: 'md',
      px: 3,
      iconSpacing: '30px',
    },
  }[size];

  const iconSize = size === 'sm' ? '16px' : '20px';
  const menuItemFontSize = size === 'sm' ? 'sm' : 'sm';
  const menuListMaxHeight = size === 'sm' ? '180px' : '200px';

  const longestOption = options.reduce(
    (longest, option) =>
      (option.searchLabel || (typeof option.label === 'string' ? option.label : '')).length >
      longest.length
        ? option.searchLabel || (typeof option.label === 'string' ? option.label : '')
        : longest,
    placeholder || '',
  );
  const estimatedWidth = `${longestOption.length * 8 + 40}px`;

  const hasMatches = isSearchable
    ? options.some((option) =>
        (option.searchLabel || (typeof option.label === 'string' ? option.label : ''))
          .toLowerCase()
          .includes(searchTerm.toLowerCase()),
      )
    : true;

  const filteredOptions = options
    .map((option) => ({
      ...option,
      isMatch: (option.searchLabel || (typeof option.label === 'string' ? option.label : ''))
        .toLowerCase()
        .includes(searchTerm.toLowerCase()),
    }))
    .sort((a, b) => {
      if (a.isMatch && !b.isMatch) return -1;
      if (!a.isMatch && b.isMatch) return 1;
      return 0;
    });

  return (
    <Box ref={menuRef} position="relative" width={isFullWidth ? '100%' : 'fit-content'}>
      {label && (
        <Text mb={2} fontSize="xs" color="#4A5769">
          {label}
        </Text>
      )}
      <Menu
        isOpen={isOpen && !isDisabled}
        onClose={onClose}
        closeOnSelect={false}
        closeOnBlur={true}
      >
        <MenuButton
          as={Button}
          rightIcon={
            !isDisabled ? <ChevronDownIcon boxSize={size === 'sm' ? '16px' : '20px'} /> : undefined
          }
          width={isFullWidth ? '100%' : estimatedWidth}
          textAlign="left"
          bg="white"
          border="1px solid #D0D5DD"
          boxShadow="0px 1px 2px 0px #1018280D"
          color="text.primary"
          display="flex"
          alignItems="center"
          cursor={isDisabled ? 'not-allowed' : 'pointer'}
          _hover={{ bg: isDisabled ? 'white' : 'gray.50' }}
          _focus={{
            outline: 'none',
            borderColor: '#D0D5DD',
            boxShadow: '0px 1px 2px 0px #1018280D',
          }}
          _disabled={{
            bg: 'white',
            color: 'text.primary',
            opacity: 1,
            cursor: 'not-allowed',
          }}
          onClick={onOpen}
          disabled={isDisabled}
          height={buttonStyles.height}
          fontSize={buttonStyles.fontSize}
          px={buttonStyles.px}
        >
          <HStack spacing={buttonStyles.iconSpacing} align="center">
            {selectedOption?.icon && <Icon as={selectedOption.icon} boxSize={iconSize} />}
            <Text fontSize={buttonStyles.fontSize} color={'text-primary'}>
              {selectedOption ? selectedOption.label : placeholder}
            </Text>
          </HStack>
        </MenuButton>
        {!isDisabled && (
          <MenuList
            borderRadius="8px"
            width={menuWidth ? `${menuWidth}px` : '100%'}
            boxShadow="lg"
            zIndex="dropdown"
            transition="all 0.2s ease-in-out"
            maxHeight={menuListMaxHeight}
            overflowY="auto"
            minWidth="unset"
            onMouseLeave={onClose}
            p={2}
          >
            {isSearchable && (
              <Box px={3} py={2}>
                <Input
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={handleSearch}
                  size="sm"
                  borderRadius="md"
                  _focus={{ borderColor: 'brand.primary.DEFAULT', boxShadow: 'none' }}
                />
              </Box>
            )}
            {options.length === 0 ? (
              <MenuItem
                fontSize={menuItemFontSize}
                color="text.secondary"
                bg="transparent"
                _hover={{ bg: 'transparent' }}
                isDisabled
              >
                {noOptionsMessage}
              </MenuItem>
            ) : isSearchable && searchTerm && !hasMatches ? (
              <MenuItem
                fontSize={menuItemFontSize}
                color="text.secondary"
                bg="transparent"
                _hover={{ bg: 'transparent' }}
                isDisabled
              >
                No data found
              </MenuItem>
            ) : (
              filteredOptions.map((option, index) => (
                <React.Fragment key={option.id}>
                  <MenuItem
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleSelect(option.id);
                    }}
                    color={
                      selectedValue === option.id
                        ? 'text.primary'
                        : option.isMatch
                          ? 'brand.primary.DEFAULT'
                          : 'text.secondary'
                    }
                    bg="transparent"
                    _hover={{
                      bg: 'transparent',
                      color: 'brand.primary.DEFAULT !important',
                      '.chakra-text': { color: 'brand.primary.DEFAULT !important' },
                      '.chakra-icon': { color: 'brand.primary.DEFAULT !important' },
                    }}
                    _focus={{ bg: 'transparent' }}
                    fontSize={menuItemFontSize}
                    minHeight={buttonStyles.height}
                    paddingY={size === 'sm' ? '2' : '8px'}
                  >
                    <HStack spacing={buttonStyles.iconSpacing} align="center" width="100%">
                      {option.icon && <Icon as={option.icon} boxSize={iconSize} />}
                      <Text>{option.label}</Text>
                    </HStack>
                  </MenuItem>
                  {index < filteredOptions.length - 1 && <MenuDivider borderColor="gray.200" />}
                </React.Fragment>
              ))
            )}
          </MenuList>
        )}
      </Menu>
    </Box>
  );
};

export default CustomSelect;
